import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import ServiceSelectionModal from 'components/modals/ServiceSelectionModal';
import AssignmentSubmissionForm from 'components/modals/AssignmentSubmissionForm';
import ClassManagementForm from 'components/modals/ClassManagementForm';
import ContactMethodModal from 'components/modals/ContactMethodModal'; // Import the new modal

const HeroSection = () => {
  const { isLoggedIn, requireAuth } = useAuth();
  const navigate = useNavigate();
  const [assignmentCount, setAssignmentCount] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [showClassManagementForm, setShowClassManagementForm] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false); // New state for contact modal

  useEffect(() => {
    // Animate assignment counter
    const timer = setInterval(() => {
      setAssignmentCount(prev => prev < 1112 ? prev + 23 : 1112);
    }, 100);

    // Update current time
    const timeTimer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      clearInterval(timer);
      clearInterval(timeTimer);
    };
  }, []);

  const handleGetHelp = () => {
    // Check if user is logged in
    if (!isLoggedIn) {
      // Redirect to login page
      navigate('/login');
    } else {
      // Show service selection modal
      setShowServiceModal(true);
    }
  };

  const handleServiceSelection = (serviceId) => {
    setShowServiceModal(false);

    // Show the appropriate form based on the selected service
    if (serviceId === 'assignment') {
      setShowAssignmentForm(true);
    } else if (serviceId === 'class-management') {
      setShowClassManagementForm(true);
    } else if (serviceId === 'consultation') {
      // Handle consultation request (e.g., open WhatsApp)
      const phoneNumber = '254793863320';
      const message = encodeURIComponent('Hi! I would like to request a consultation about your academic services. Can we discuss my specific needs?');
      window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
    }
  };

  const handleChatWithUs = () => { // New handler for "Chat with Us" button
    setShowContactModal(true);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 sm:pt-8">
      {/* Modals */}
      <ServiceSelectionModal 
        isOpen={showServiceModal} 
        onClose={() => setShowServiceModal(false)} 
        onSelectService={handleServiceSelection} 
      />

      <AssignmentSubmissionForm 
        isOpen={showAssignmentForm} 
        onClose={() => setShowAssignmentForm(false)} 
        onSubmit={() => {
          // Handle successful submission (e.g., show a success message)
        }} 
      />

      <ClassManagementForm 
        isOpen={showClassManagementForm} 
        onClose={() => setShowClassManagementForm(false)} 
        onSubmit={() => {
          // Handle successful submission (e.g., show a success message)
          alert('Your class management request has been submitted successfully! Our team will contact you shortly.');
        }} 
      />

      <ContactMethodModal // Render the new contact modal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />

      {/* Enhanced Animated Background */}
      <div className="absolute inset-0" data-no-download="true" data-idm-ignore="true">
        {/* Animated gradient circles */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-gradient-to-br from-primary/20 to-secondary/10 rounded-full blur-3xl animate-blob animation-delay-1000"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-gradient-to-br from-secondary/20 to-accent/10 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute top-3/4 left-1/2 w-[400px] h-[400px] bg-gradient-to-br from-accent/20 to-primary/10 rounded-full blur-3xl animate-blob animation-delay-3000"></div>

        {/* Light beams */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent opacity-30 animate-gradient-y" data-no-download="true"></div>

        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-20" data-no-download="true" data-idm-ignore="true">
          <div className="w-full h-full bg-gradient-to-br from-white/[0.02] to-transparent"></div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          {/* Logo */}
          <div className="flex items-center justify-center space-x-4 mb-10">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/50 to-secondary/50 rounded-2xl blur-xl animate-pulse-slow"></div>
              <div className="relative w-20 h-20 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center shadow-xl group-hover:shadow-primary/50 transition-all duration-300 backdrop-blur-md bg-white/10 border border-white/20">
                <Icon name="GraduationCap" size={36} color="currentColor" className="group-hover:scale-110 transition-transform duration-300" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold font-accent">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-secondary to-accent animate-gradient-x">SOMO</span>
            </h1>
          </div>

          {/* Main Headlines */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-6xl md:text-8xl font-extrabold text-text-primary mb-6 leading-tight tracking-tight">
              Your Trusted Academic
              <div className="relative inline-block">
                <span className="relative z-10 bg-clip-text text-transparent bg-gradient-to-r from-primary via-secondary to-accent animate-gradient-x block">Partner</span>
                <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-accent rounded-full"></div>
              </div>
            </h2>
          </motion.div>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl md:text-2xl text-text-secondary mb-10 max-w-3xl mx-auto leading-relaxed"
          >
            Experience <span className="text-accent font-semibold">peace of mind</span> and <span className="text-accent font-semibold">Time Freedom</span> as we handle your assignments, giving you space to focus on what truly matters.
            <br />
            <br />
            <span className="inline-flex items-center space-x-3 text-white/70">
              <span>No Hidden Fees</span>
              <span className="w-1.5 h-1.5 rounded-full bg-white/30"></span>
              <span>Instant Support</span>
            </span>
          </motion.p>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-16"
        >
          <button
            onClick={handleGetHelp}
            className="relative group overflow-hidden bg-gradient-to-r from-primary to-secondary text-white font-bold text-lg px-12 py-6 rounded-2xl shadow-lg btn-ios-feedback magnetic-btn glass-card"
          >
            {/* Shimmer effect */}
            <div className="absolute inset-0 -top-2 -bottom-2 opacity-30 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
            
            {/* Button background animation */}
            <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_100%] animate-gradient-x"></div>

            {/* Premium glow effect */}
            <div className="absolute inset-0 rounded-2xl animate-glow-pulse opacity-60"></div>

            {/* Button content */}
            <div className="relative z-10 flex items-center space-x-3">
              <div className="bg-white/25 backdrop-blur-sm rounded-full p-2 animate-float">
                <Icon name="Rocket" size={20} className="text-white" />
              </div>
              <span className="tracking-wide">Get Started</span>
              <div className="w-2 h-2 bg-white/60 rounded-full animate-pulse"></div>
            </div>
          </button>

          <button
            onClick={handleChatWithUs}
            className="relative group overflow-hidden glass-card text-white font-semibold text-lg px-12 py-6 rounded-2xl btn-ios-feedback hover-lift flex items-center space-x-3"
          >
            <div className="bg-accent/25 backdrop-blur-sm rounded-full p-2">
              <Icon name="MessageSquare" size={20} className="text-accent animate-pulse" />
            </div>
            <span className="tracking-wide">Chat with Us</span>
            
            {/* Ripple effect on hover */}
            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <div className="absolute inset-0 bg-gradient-to-r from-accent/10 via-accent/5 to-transparent animate-shimmer"></div>
            </div>
          </button>
        </motion.div>

        {/* Live Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
        >
          <motion.div 
            className="glass-card p-8 text-center hover-lift group relative overflow-hidden border border-white/10"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            {/* Floating background orb */}
            <div className="absolute top-4 right-4 w-12 h-12 bg-accent/20 rounded-full blur-xl animate-float"></div>
            
            <div className="text-4xl font-bold text-accent mb-3 relative">
              <span className="animate-scale-in">{assignmentCount.toLocaleString()}+</span>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent/60 rounded-full animate-pulse"></div>
            </div>
            <div className="text-text-secondary font-medium mb-1">Assignments Completed</div>
            <div className="text-sm text-text-muted flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-accent/60 rounded-full animate-pulse"></div>
              <span>This Month</span>
            </div>
            
            {/* Shimmer overlay */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-accent/5 to-transparent animate-shimmer"></div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-card p-8 text-center hover-lift group relative overflow-hidden border border-white/10"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300, delay: 0.1 }}
          >
            {/* Floating background orb */}
            <div className="absolute top-4 right-4 w-12 h-12 bg-primary/20 rounded-full blur-xl animate-float animation-delay-1000"></div>
            
            <div className="text-4xl font-bold text-primary mb-3 relative">
              <span className="animate-spring-in">2 Min</span>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary/60 rounded-full animate-pulse animation-delay-500"></div>
            </div>
            <div className="text-text-secondary font-medium mb-2">Average Response</div>
            <div className="flex items-center justify-center mt-2">
              <div className="w-3 h-3 bg-success rounded-full animate-glow-pulse mr-2"></div>
              <span className="text-sm text-success font-medium tracking-wide">Online Now</span>
            </div>
            
            {/* Shimmer overlay */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent animate-shimmer"></div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-card p-8 text-center hover-lift group relative overflow-hidden border border-white/10"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300, delay: 0.2 }}
          >
            {/* Floating background orb */}
            <div className="absolute top-4 right-4 w-12 h-12 bg-secondary/20 rounded-full blur-xl animate-float animation-delay-2000"></div>
            
            <div className="text-4xl font-bold text-secondary mb-3 relative">
              <span className="animate-float-in">24/7</span>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-secondary/60 rounded-full animate-pulse animation-delay-1000"></div>
            </div>
            <div className="text-text-secondary font-medium mb-1">Support Available</div>
            <div className="text-sm text-text-muted flex items-center justify-center space-x-2">
              <Icon name="Clock" size={14} className="text-secondary/60 animate-rotate-slow" />
              <span>{currentTime.toLocaleTimeString()}</span>
            </div>
            
            {/* Shimmer overlay */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-secondary/5 to-transparent animate-shimmer"></div>
            </div>
          </motion.div>
        </motion.div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-12 flex flex-wrap items-center justify-center space-x-8 opacity-60"
        >
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="Shield" size={20} color="var(--color-success)" />
            <span className="text-sm text-text-secondary">100% Plagiarism Free</span>
          </div>
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="Clock" size={20} color="var(--color-accent)" />
            <span className="text-sm text-text-secondary">On-Time Delivery</span>
          </div>
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="DollarSign" size={20} color="var(--color-primary)" />
            <span className="text-sm text-text-secondary">Money Back Guarantee</span>
          </div>
        </motion.div>
      </div>

      {/* Modern Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-16 sm:bottom-10 left-1/2 transform -translate-x-1/2 z-20 hidden sm:flex"
      >
        <div className="flex flex-col items-center">
          {/* Text with gradient */}
          <motion.span 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.2 }}
            className="text-sm font-medium bg-clip-text text-transparent bg-gradient-to-r from-primary/80 to-secondary/80 mb-3"
          >
            Scroll to explore
          </motion.span>

          {/* Animated scroll indicator */}
          <div className="relative h-16 w-8 flex justify-center">
            {/* Outer container with glassmorphism */}
            <div className="absolute inset-0 rounded-full backdrop-blur-sm bg-white/5 border border-white/10"></div>

            {/* Animated dot */}
            <motion.div
              animate={{ 
                y: [0, 20, 0],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
                ease: "easeInOut" 
              }}
              className="absolute top-2 w-2 h-2 rounded-full bg-gradient-to-r from-primary to-secondary shadow-glow"
            ></motion.div>

            {/* Subtle glow effect */}
            <motion.div
              animate={{ 
                opacity: [0.3, 0.6, 0.3]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
                ease: "easeInOut" 
              }}
              className="absolute inset-0 rounded-full bg-primary/10 blur-md"
            ></motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
